// The lot form's reads offline (BL-2437, ADR 066 — Offline Mode § 3): the lot itself, its notes,
// the pickers (seller, location, tax rate, sales rep) and the status menu's check, answered from
// what the sync engine stored (BL-2344). Each answer is the online route's own DTO. A shape the
// device can't answer (another note entity, a contact search) fails as a dropped connection.
import { hasAnyPermission } from '@auctogon/core-admin/api/permissions';
import type { LocationDto } from '@auctogon/core/schemas/responses/location';
import { batchActionPermissions } from '@auctogon/admin-isomorphic/isomorphic/lots/batch-action-permissions';
import { buildOfflineSetStatusCheck } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-batch-check';
import { matchOfflineSellers, sortOfflineSellers } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-seller-search';
import type { CustomerSearchResultDto } from '@auctogon/core-admin/schemas/responses/customer-search';

import { OFFLINE_SESSION_RECORD_KEY, type OfflineReferenceRecord, type OfflineStore } from '../../gateways/native-device';
import { OFFLINE_REFERENCE_KEY } from '../../stores/offline-sync/offline-sync-engine';
import type { OfflineRequest } from './create-offline-transport';
import { NOT_FOUND, SIGNED_OUT } from './offline-answers';
import { rememberNoteOwners } from './offline-note-handlers';
import { withCachedDetailThumbnails } from './offline-thumbnails';


/** A request shape the device can't answer: fail it as the network would. */
function unanswerable(): never {
  throw new TypeError('Failed to fetch');
}

async function withStore<T>(request: OfflineRequest, body: (store: OfflineStore) => Promise<T>): Promise<T | typeof SIGNED_OUT> {
  const store = await request.openStore();
  return store ? body(store) : SIGNED_OUT;
}

async function referenceRecord(store: OfflineStore): Promise<OfflineReferenceRecord | null> {
  return store.records.get({ collection: 'reference', key: OFFLINE_REFERENCE_KEY });
}

async function reference(store: OfflineStore): Promise<OfflineReferenceRecord['reference'] | null> {
  return (await referenceRecord(store))?.reference ?? null;
}

/**
 * The synced sellers in search order, sorted once per reference sync (keyed by scope, sync time and
 * etag) rather than on every keystroke of the picker.
 */
let sortedSellersCache: { key: string; sellers: CustomerSearchResultDto[] } | null = null;

/**
 * Test seam: a test reseeding different sellers under the same scope and sync stamp.
 *
 * @returns nothing.
 * @sideEffect Drops the sorted-seller cache.
 */
export function __resetOfflineSellersCacheForTest(): void {
  sortedSellersCache = null;
}

async function sortedSellers(store: OfflineStore): Promise<CustomerSearchResultDto[]> {
  const record = await referenceRecord(store);
  const key = `${store.scope.tenantId}:${store.scope.userId}|${record?.syncedAt ?? ''}|${record?.etag ?? ''}`;
  if (sortedSellersCache?.key === key) return sortedSellersCache.sellers;
  const sellers = sortOfflineSellers(record?.reference.sellers ?? []);
  sortedSellersCache = { key, sellers };
  return sellers;
}

/**
 * The location picker's list: the house's, plus the named customer's (`customerId`), or that
 * customer's alone (`customerOnly`), as `GET /api/locations` answers it. The reference holds
 * active rows only, so an archived current location (`currentLocationId`) is never added: the
 * form shows the saved value itself (BL-2437, decision Q10).
 */
function pickLocations(locations: readonly LocationDto[], query: URLSearchParams): LocationDto[] {
  const customerId = query.get('customerId') || null;
  const customerOnly = query.get('customerOnly') === 'true';
  return locations.filter((loc) => {
    if (loc.customerId == null) return !customerOnly;
    return customerId != null && loc.customerId === customerId;
  });
}

/** Whether the stored session's role at this tenant may run `set-status`. */
async function viewerMaySetStatus(store: OfflineStore): Promise<boolean> {
  const session = await store.records.get({ collection: 'session', key: OFFLINE_SESSION_RECORD_KEY });
  if (!session) return false;
  const roles = session.user.tenantRoles[store.scope.tenantId] ?? [];
  return hasAnyPermission(roles, batchActionPermissions('set-status'), session.user.isSuperAdmin);
}

function recordIdsOf(body: unknown): string[] {
  if (body == null || typeof body !== 'object' || !('recordIds' in body) || !Array.isArray(body.recordIds)) return [];
  return body.recordIds.filter((id): id is string => typeof id === 'string');
}

/** The lot form's reads, keyed `"<registry key>:<VERB>"`; typed by `OfflineHandlers` where composed. */
export const lotFormReadHandlers = {
  'lots.id.index:GET': (req: OfflineRequest) =>
    withStore(req, async (store) => {
      const lot = req.params.id ? await store.records.get({ collection: 'lots', key: req.params.id }) : null;
      if (!lot) return NOT_FOUND;
      return { kind: 'ok' as const, data: await withCachedDetailThumbnails({ store, detail: lot.detail }) };
    }),

  // The lot form's notes panel is the only note surface offline.
  'internal_notes.index:GET': (req: OfflineRequest) =>
    withStore(req, async (store) => {
      const entityId = req.query.get('entityId');
      if (req.query.get('entityType') !== 'lot' || !entityId) return unanswerable();
      const lot = await store.records.get({ collection: 'lots', key: entityId });
      if (!lot) return NOT_FOUND;
      rememberNoteOwners({ scope: store.scope, lotId: entityId, notes: lot.notes });
      return { kind: 'ok' as const, data: lot.notes };
    }),

  // The seller picker only: the Contacts pickers (`include=contact`) are disabled offline.
  'customers.search:GET': (req: OfflineRequest) =>
    withStore(req, async (store) => {
      if (req.query.get('include') !== 'seller' || req.query.has('id')) return unanswerable();
      const sellers = await sortedSellers(store);
      return { kind: 'ok' as const, data: matchOfflineSellers({ sellers, search: req.query.get('search'), presorted: true }) };
    }),

  'locations.index:GET': (req: OfflineRequest) =>
    withStore(req, async (store) => ({
      kind: 'ok' as const,
      data: pickLocations((await reference(store))?.locations ?? [], req.query),
    })),

  'tenants.id.tax_rates.index:GET': (req: OfflineRequest) =>
    withStore(req, async (store) => ({ kind: 'ok' as const, data: (await reference(store))?.taxRates ?? [] })),

  'sales_reps.index:GET': (req: OfflineRequest) =>
    withStore(req, async (store) => ({ kind: 'ok' as const, data: (await reference(store))?.salesReps ?? [] })),

  // The status menu's check (ADR 033 — Transition Service): Draft ↔ Upcoming, by the server's guard.
  'lots.batch_check:POST': (req: OfflineRequest) =>
    withStore(req, async (store) => {
      const ids = recordIdsOf(req.body);
      const found = await store.records.getMany({ collection: 'lots', keys: ids });
      const lots = found.flatMap((lot) => (lot ? [lot.detail] : []));
      const auctionIds = [...new Set(lots.flatMap((lot) => (lot.auctionId ? [lot.auctionId] : [])))];
      const auctions = await store.records.getMany({ collection: 'auctions', keys: auctionIds });
      const phaseOf = new Map(auctions.flatMap((a) => (a ? [[a.auction.id, a.auction.phaseKind] as const] : [])));
      const zoneOf = new Map(lots.flatMap((lot) => (lot.auctionId ? [[lot.auctionId, lot.auctionTimeZone] as const] : [])));
      return {
        kind: 'ok' as const,
        data: buildOfflineSetStatusCheck({
          lots: lots.map((lot) => ({ id: lot.id, phaseKind: lot.phaseKind, auctionId: lot.auctionId })),
          auctionPhaseKindOf: (id) => phaseOf.get(id) ?? null,
          auctionTimeZoneOf: (id) => zoneOf.get(id) ?? null,
          viewerMayRunSetStatus: await viewerMaySetStatus(store),
        }),
      };
    }),
};
